// Pure helpers for the V3 people and contact views (M08–M10, M50–M57, M112); no network, no storage.
import { own, text, contactGroups } from "./memberDerive";

export const INTERESTS = ["soziales", "sport", "kultur", "bildung", "freizeit", "regional"];
const str = (v) => (typeof v === "string" ? v.trim() : "");
const fold = (v) => str(v).toLocaleLowerCase("de").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export function shortName(name) {
  const parts = str(name).split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  return parts.length === 1 ? parts[0] : `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

export const initialsOf = (name) => str(name).split(/\s+/).filter(Boolean).map((p) => Array.from(p)[0]).join("").slice(0, 2).toUpperCase() || "M";

// Only the documented category keys; anything else from the Core is not shown as an interest.
export const knownInterests = (list) => (Array.isArray(list) ? list.filter((k) => INTERESTS.includes(k)) : []);

export function interestText(list, label) {
  const keys = knownInterests(list);
  return keys.length ? keys.map(label).join(" · ") : "";
}

export function personContext(m, label) {
  return [str(own(m, "city")), interestText(own(m, "interests"), label)].filter(Boolean).join(" · ") || "Keine weiteren Angaben";
}

export function sharedInterests(mine, theirs) {
  const a = knownInterests(mine);
  return knownInterests(theirs).filter((k) => a.includes(k));
}

// Filter lives in the URL only (M50): /menschen?ort=&interesse=&sprache=&q=
export function readPeopleFilter(search) {
  const p = new URLSearchParams(typeof search === "string" ? search : "");
  const interest = INTERESTS.includes(p.get("interesse")) ? p.get("interesse") : "";
  return { city: (p.get("ort") || "").slice(0, 80), interest, language: (p.get("sprache") || "").slice(0, 20), q: (p.get("q") || "").slice(0, 100) };
}

export function peopleQuery({ city, interest, language, q }) {
  const p = new URLSearchParams();
  if (city) p.set("ort", city);
  if (interest) p.set("interesse", interest);
  if (language) p.set("sprache", language);
  if (q && q.trim()) p.set("q", q.trim());
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const peopleFilterCount = (f) => ["city", "interest", "language"].filter((k) => f[k]).length;

export function matchPerson(m, q) {
  const needle = fold(q);
  if (!needle) return true;
  return [own(m, "name"), own(m, "city"), text(own(m, "bio"))].some((v) => fold(v).includes(needle));
}

// Interest tab (M08): how many listed members name each documented interest.
export function interestCounts(members) {
  return INTERESTS.map((k) => [k, (Array.isArray(members) ? members : []).filter((m) => knownInterests(own(m, "interests")).includes(k)).length]).filter(([, n]) => n > 0);
}

const KIND = { incoming: "Eingehend", outgoing: "Gesendet", accepted: "Bestätigt" };
const STATE = { incoming: "Anfrage ansehen", outgoing: "Ausstehend", accepted: "Nachricht schreiben" };

export const contactHref = (kind, id) => {
  const e = encodeURIComponent(id);
  return kind === "incoming" ? `/kontakte/anfragen/${e}` : kind === "outgoing" ? `/kontakte/gesendet/${e}` : `/nachrichten/${e}`;
};

// M10: one row per direct conversation, grouped incoming → sent → confirmed; nothing is merged or invented.
export function contactRows(conversations, q = "") {
  const g = contactGroups(Array.isArray(conversations) ? conversations : []);
  return ["incoming", "outgoing", "accepted"].flatMap((kind) => g[kind]
    .filter((c) => c && c.other && str(c.other.name) && matchPerson(c.other, q))
    .map((c) => ({ kind, id: String(c.id), title: KIND[kind], name: shortName(c.other.name), context: `${shortName(c.other.name)} · ${STATE[kind]}`, to: contactHref(kind, c.id), other: c.other })));
}

export function findContact(conversations, id, kind) {
  const g = contactGroups(Array.isArray(conversations) ? conversations : []);
  return (g[kind] || []).find((c) => String(c.id) === String(id)) || null;
}

// Core 1.1 has no "sent at" field for contact requests; only a valid own ISO value is shown.
export function sentAt(conv) {
  const v = own(conv, "created_at");
  const d = typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v) ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return "Nicht angegeben";
  const f = new Intl.DateTimeFormat("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month}.${p.year} · ${p.hour}:${p.minute}`;
}

// GET /member/blocks → blocks[{member_id, name}]; incomplete entries are dropped.
export function normalizeBlocks(d) {
  const list = own(d, "blocks");
  if (!Array.isArray(list)) return null;
  return list.filter((b) => (typeof own(b, "member_id") === "string" || Number.isInteger(own(b, "member_id"))) && str(own(b, "name")))
    .map((b) => ({ id: String(b.member_id), name: str(b.name) }));
}
